/**
 * Analytics Service
 * Provides centralized analytics tracking for the application
 * Supports Google Analytics 4 and custom event tracking
 * GA tag is loaded in public/index.html (G-R3W4L4K4K7); this service uses the same ID for events.
 */

// Use env override so you can disable in dev (e.g. REACT_APP_GA_MEASUREMENT_ID=) or use a different property
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID !== undefined
  ? process.env.REACT_APP_GA_MEASUREMENT_ID
  : 'G-R3W4L4K4K7';

let isInitialized = false;

export const sanitizeAnalyticsPath = (pathname = '/') => {
  const path = String(pathname || '/');
  if (path.startsWith('/pulse/signups/')) return '/pulse/signups';
  return path || '/';
};

export const initializeAnalytics = () => {
  if (typeof window === 'undefined') return;
  if (isInitialized) return;
  if (!GA_MEASUREMENT_ID) return;
  const safePath = sanitizeAnalyticsPath(window.location.pathname);
  const safeLocation = `${window.location.origin}${safePath}`;
  // If gtag already exists (e.g. from index.html), just mark initialized and use it
  if (typeof window.gtag !== 'undefined') {
    isInitialized = true;
    return;
  }
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script1);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: safePath,
    page_location: safeLocation
  });
  isInitialized = true;
};

const isGAAvailable = () =>
  typeof window !== 'undefined' &&
  typeof window.gtag !== 'undefined' &&
  GA_MEASUREMENT_ID;

export const trackPageView = (pagePath, pageTitle = null) => {
  if (isGAAvailable()) {
    const path = sanitizeAnalyticsPath(pagePath || (typeof window !== 'undefined' ? window.location.pathname : '/'));
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      page_location: origin ? `${origin}${path}` : path,
      page_title: pageTitle || document.title,
    });
  }
};

export const trackEvent = (eventName, eventParams = {}) => {
  if (isGAAvailable()) {
    window.gtag('event', eventName, eventParams);
  }
};

export const trackCoverSelection = (coverType) => {
  trackEvent('cover_selected', {
    cover_type: coverType,
    event_category: 'Product Selection',
    event_label: `${coverType} Cover Selected`,
  });
};

export const trackClassSelection = (coverType, vehicleClass) => {
  trackEvent('class_selected', {
    cover_type: coverType,
    vehicle_class: vehicleClass,
    event_category: 'Product Selection',
    event_label: `${vehicleClass} Selected`,
  });
};

export const trackStepProgress = (step, stepNumber) => {
  trackEvent('step_progress', {
    step_name: step,
    step_number: stepNumber,
    event_category: 'User Journey',
    event_label: `Step ${stepNumber}: ${step}`,
  });
};

export const trackFormCompletion = (formType, additionalData = {}) => {
  trackEvent('form_completed', {
    form_type: formType,
    ...additionalData,
    event_category: 'Form Interaction',
    event_label: `${formType} Form Completed`,
  });
};

/**
 * Track payment initiation
 * @param {object} paymentData - Payment information (coverType, vehicleClass, premiumAmount)
 */
export const trackPaymentInitiated = (paymentData) => {
  trackEvent('payment_initiated', {
    cover_type: paymentData.coverType,
    vehicle_class: paymentData.vehicleClass,
    premium_amount: paymentData.premiumAmount,
    event_category: 'Payment',
    event_label: 'Payment Initiated',
    value: paymentData.premiumAmount,
    currency: 'KES',
  });
};

export const trackPaymentCompleted = (paymentData) => {
  trackEvent('payment_completed', {
    cover_type: paymentData.coverType,
    vehicle_class: paymentData.vehicleClass,
    premium_amount: paymentData.premiumAmount,
    client_id: paymentData.clientId,
    event_category: 'Payment',
    event_label: 'Payment Completed',
    value: paymentData.premiumAmount,
    currency: 'KES',
  });
};

export const trackPaymentFailed = (errorData) => {
  trackEvent('payment_failed', {
    error_message: errorData.message,
    error_code: errorData.code,
    cover_type: errorData.coverType,
    event_category: 'Payment',
    event_label: 'Payment Failed',
  });
};

export const trackAdminAction = (action) => {
  trackEvent('admin_action', {
    action,
    event_category: 'Admin',
    event_label: `Admin: ${action}`,
  });
};

export const trackError = (errorType, errorMessage, additionalData = {}) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage,
    ...additionalData,
    event_category: 'Error',
    event_label: `${errorType}: ${errorMessage}`,
  });
};

export const trackEngagement = (engagementType, engagementData = {}) => {
  trackEvent('user_engagement', {
    engagement_type: engagementType,
    ...engagementData,
    event_category: 'Engagement',
    event_label: engagementType,
  });
};

export const trackTimeOnPage = (pagePath, timeSpent) => {
  trackEvent('time_on_page', {
    page_path: pagePath,
    time_spent: timeSpent,
    event_category: 'Engagement',
    event_label: `Time on ${pagePath}`,
  });
};

export const trackWhatsAppSupport = () => {
  trackEvent('whatsapp_support_clicked', {
    event_category: 'Support',
    event_label: 'WhatsApp Support Clicked',
  });
};

export const trackQuoteGenerated = () => {
  trackEvent('quote_generated', {
    event_category: 'Quote',
    event_label: 'Quote Generated',
  });
};

export const trackInsuranceCheck = (checkData) => {
  trackEvent('insurance_check_performed', {
    registration_number: checkData.registrationNumber,
    has_existing_insurance: checkData.hasExistingInsurance,
    event_category: 'Vehicle Check',
    event_label: 'Insurance Check Performed',
  });
};
